import React from 'react';
import { useForm } from 'react-hook-form';
import { Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { useObjectives } from './ObjectivesContext.jsx';
import { useAuth } from 'contexts/AuthContext';
import budgetService from 'services/budgetService';
import objectivesService from 'services/objectivesService';
import { toast } from 'react-toastify';

const defaultValues = {
  client: '',
  account: '',
  accountIdentifier: '',
  medium: '',
  campaignType: '',
  unit: 'Leads',
  startDate: '',
  endDate: '',
  desiredFrequency: 'Semanal',
  segment: '',
  owner: '',
  nominalValue: 0,
  objectiveType: 'Nominal',
  urgency: 'Media',
  accumulated: false,
  comments: ''
};

const CreateObjectiveForm = ({ onCreated, campaignFilter }) => {
  const { createObjective, updateObjective } = useObjectives();
  const { filterByAssignedClients } = useAuth();
  const { register, handleSubmit, reset, setValue, watch } = useForm({ defaultValues });
  const [clients, setClients] = React.useState([]);
  const [accounts, setAccounts] = React.useState([]);
  const [loadingClients, setLoadingClients] = React.useState(false);
  const [loadingAccounts, setLoadingAccounts] = React.useState(false);
  const [selectedClientId, setSelectedClientId] = React.useState('');
  const [editingObjective, setEditingObjective] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    const loadClients = async () => {
      try {
        setLoadingClients(true);
        // Preferir endpoint público rápido si existe, si no usar presupuesto service alterno
        const result = await budgetService.getClients();
        if (result.success) {
          // Filtrar clientes por los asignados al usuario
          const filteredClients = filterByAssignedClients(result.clients, 'ClientID');
          setClients(filteredClients);
        }
      } catch (_) {
        // noop
      } finally {
        setLoadingClients(false);
      }
    };
    loadClients();
  }, [filterByAssignedClients]);

  // Escuchar evento para cargar objetivo en modo edición
  React.useEffect(() => {
    const handleEdit = async (e) => {

      const objectiveData = e.detail || {};
      const objectiveId = objectiveData.id;
      
      if (!objectiveId) {
        return;
      }

      try {
        // Obtener el objetivo completo desde el backend
        const objectiveResponse = await objectivesService.getById(objectiveId);
        
        if (!objectiveResponse.success) {
          console.error('❌ API ERROR - Error obteniendo objetivo:', objectiveResponse.error);
          return;
        }

        const o = objectiveResponse.data;
        setEditingObjective(o);
        setSelectedClientId(String(o.clientId || ''));
        
        // Cargar cuentas del cliente y preseleccionar
        if (o.clientId) {
          try {
            setLoadingAccounts(true);
            const resp = await budgetService.getAccountsByClient(o.clientId);
            
            if (resp.success) {
              // Manejar estructura anidada: resp.data.accounts o resp.accounts
              const list = resp.data?.accounts || resp.accounts || [];
              
              // SOLUCIÓN INTELIGENTE: Si estamos editando, detectar el campaignType correcto basándose en la cuenta objetivo
              let effectiveCampaignFilter = campaignFilter;
              
              if (editingObjective && o.accountId) {
                // Buscar la cuenta objetivo en la lista sin filtrar para determinar su campaignType
                const targetAccount = list.find(a => String(a.accountId) === String(o.accountId));
                if (targetAccount) {
                  effectiveCampaignFilter = targetAccount.campaignType;
                } else {
                  // Fallback: usar campaignType del objetivo si existe
                  effectiveCampaignFilter = o.campaignType || campaignFilter;
                }
              }
              
              const filtered = effectiveCampaignFilter ? 
                list.filter(a => (a.campaignType || a.CampaignType || '').toLowerCase() === effectiveCampaignFilter.toLowerCase()) : 
                list;
              
              setAccounts(filtered);
              const acc = filtered.find(a => String(a.accountId) === String(o.accountId));
              
              if (acc) {
                setValue('accountId', String(acc.accountId));
                const platform = acc.accountType || '';
                const campaignType = acc.campaignType || '';
                setValue('medium', platform);
                setValue('campaignType', campaignType);
                const rawIdentifier = acc.accountIdentifier || '';
                const identifierDisplay = (platform === 'Meta' && rawIdentifier.startsWith('act_')) ? rawIdentifier.slice(4) : rawIdentifier;
                setValue('accountIdentifier', identifierDisplay);
              } else {
                // Fallback a los valores del objeto original
                setValue('accountId', String(o.accountId || ''));
                setValue('medium', o.medium || '');
                setValue('campaignType', o.campaignType || '');
                setValue('accountIdentifier', o.accountIdentifier || '');
              }
            } else {
              console.error('❌ ACCOUNTS API ERROR - getAccountsByClient falló:', resp);
            }
          } catch (err) {
            console.error('❌ ACCOUNTS EXCEPTION - Error cargando cuentas para edición:', err);
          } finally {
            setLoadingAccounts(false);
          }
        }
        
        // Llenar formulario
        setValue('clientId', o.clientId || '');
        
        // Formatear fechas correctamente para inputs de fecha
        const formatDateForInput = (dateStr) => {
          if (!dateStr) return '';
          try {
            if (typeof dateStr === 'string' && dateStr.includes('T')) {
              // Para fechas UTC, extraer solo la parte de fecha sin conversión de zona horaria
              const date = new Date(dateStr);
              const year = date.getUTCFullYear();
              const month = String(date.getUTCMonth() + 1).padStart(2, '0');
              const day = String(date.getUTCDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            }
            if (typeof dateStr === 'string' && dateStr.includes('-') && dateStr.length === 10) {
              return dateStr; // Ya está en formato YYYY-MM-DD
            }
            return dateStr;
          } catch (e) {
            return dateStr;
          }
        };
        
        const formattedStartDate = formatDateForInput(o.startDate);
        const formattedEndDate = formatDateForInput(o.endDate);
        
        setValue('startDate', formattedStartDate);
        setValue('endDate', formattedEndDate);
        setValue('unit', o.unit || 'Leads');
        setValue('desiredFrequency', o.desiredFrequency || 'Semanal');
        setValue('segment', o.segment || '');
        setValue('owner', o.owner || '');
        setValue('nominalValue', o.nominalValue || 0);
        setValue('objectiveType', o.objectiveType || 'Nominal');
        setValue('urgency', o.urgency || 'Media');
        setValue('accumulated', !!o.accumulated);
        setValue('comments', o.comments || '');
        
      } catch (error) {
        console.error('❌ HANDLE EDIT ERROR - Error en handleEdit:', error);
        toast.error('Error cargando objetivo para edición');
      }
    };
    
    window.addEventListener('objective:edit', handleEdit);
    return () => window.removeEventListener('objective:edit', handleEdit);
  }, [setValue, campaignFilter]);

  // Efecto para cargar cuentas cuando cambia selectedClientId
  React.useEffect(() => {
    if (selectedClientId && !loadingAccounts) {
      handleClientChange({ target: { value: selectedClientId } });
    }
  }, [selectedClientId]);

  // Efecto para preseleccionar cuenta cuando se cargan las cuentas y hay un objetivo en edición
  React.useEffect(() => {
    
    if (accounts.length > 0 && editingObjective && editingObjective.accountId) {
      
      const acc = accounts.find(a => String(a.accountId) === String(editingObjective.accountId));
      
      if (acc) {
        setValue('accountId', String(acc.accountId));
        const platform = acc.accountType || '';
        const campaignType = acc.campaignType || '';
        setValue('medium', platform);
        setValue('campaignType', campaignType);
        const rawIdentifier = acc.accountIdentifier || '';
        const identifierDisplay = (platform === 'Meta' && rawIdentifier.startsWith('act_')) ? rawIdentifier.slice(4) : rawIdentifier;
        setValue('accountIdentifier', identifierDisplay);

      } else {
      }
    }
  }, [accounts, editingObjective, setValue]);

  const handleClientChange = async (e) => {
    const clientId = e.target.value;
    
    setSelectedClientId(clientId);
    setAccounts([]);
    setValue('accountId', '');
    setValue('medium', '');
    setValue('campaignType', '');
    
    if (!clientId) {
      return;
    }
    
    try {
      setLoadingAccounts(true);
      const resp = await budgetService.getAccountsByClient(clientId);
      
      if (resp.success) {
        // Manejar estructura anidada: resp.data.accounts o resp.accounts
        const list = resp.data?.accounts || resp.accounts || [];
        const filtered = campaignFilter ? list.filter(a => (a.campaignType || a.CampaignType || '').toLowerCase() === campaignFilter.toLowerCase()) : list;
        setAccounts(filtered);
      }
    } catch (error) {
      console.error('❌ CLIENT CHANGE ERROR - Error cargando cuentas:', error);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const onSubmit = async (data) => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      // Enriquecer payload con nombres legibles
      const selectedClient = clients.find(c => String(c.clientKey || c.ClientID) === String(selectedClientId));
      const selectedAccount = accounts.find(a => String(a.accountId) === String(watch('accountId')));

      const payload = {
        // Campos requeridos por backend
        clientId: selectedClient ? (selectedClient.clientKey || selectedClient.ClientID) : data.clientId,
        clientName: selectedClient ? (selectedClient.clientName || selectedClient.ClientName) : data.clientName,
        accountId: selectedAccount ? selectedAccount.accountId : data.accountId,
        accountName: selectedAccount ? selectedAccount.accountName : data.accountName,
        medium: watch('medium') || selectedAccount?.accountType || data.medium,
        campaignType: watch('campaignType') || selectedAccount?.campaignType || data.campaignType,
        // Otros campos del objetivo
        unit: data.unit,
        startDate: data.startDate,
        endDate: data.endDate,
        desiredFrequency: data.desiredFrequency,
        segment: data.segment,
        owner: data.owner,
        nominalValue: data.nominalValue,
        objectiveType: data.objectiveType,
        urgency: data.urgency,
        accumulated: data.accumulated,
        comments: data.comments,
        // Solo visual, no lo usa el backend
        accountIdentifier: data.accountIdentifier
      };

      let result;
      if (editingObjective) {
        // Modo edición
        result = await updateObjective(editingObjective.id, payload);
        toast.success('Objetivo actualizado correctamente');
      } else {
        // Modo creación
        result = await createObjective(payload);
        toast.success('Objetivo creado correctamente');
      }
      
      if (onCreated) onCreated(result);
      
      // Reset completo del formulario y filtros visuales
      reset(defaultValues);
      setSelectedClientId('');
      setAccounts([]);
      setEditingObjective(null);
    } catch (error) {
      console.error('Error guardando objetivo:', error);
      toast.error('Error guardando el objetivo');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="mb-3">
      <Card.Header>
        <h5 className="mb-0">{editingObjective ? 'Editar Objetivo' : 'Nuevo Objetivo de Cliente'}</h5>
        <small>{editingObjective ? 'Modifica los campos necesarios para actualizar el objetivo' : 'Completa todos los campos para crear un nuevo objetivo'}</small>
      </Card.Header>
      <Card.Body>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row className="g-3">
            <Col md={6}>
              <Form.Label>Cliente</Form.Label>
              <Form.Select {...register('clientId')} onChange={handleClientChange} value={selectedClientId}>
                <option value="">Seleccionar cliente</option>
                {clients.map(c => (
                  <option key={c.clientKey || c.ClientID} value={c.clientKey || c.ClientID}>
                    {c.clientName || c.ClientName}
                  </option>
                ))}
              </Form.Select>
              {loadingClients && <Spinner size="sm" className="ms-2" />}
            </Col>
            <Col md={6}>
              <Form.Label>Cuenta</Form.Label>
              <Form.Select {...register('accountId')} disabled={!selectedClientId || loadingAccounts} onChange={(e)=>{
                const id = e.target.value; setValue('accountId', id);
                const acc = (accounts || []).find(a => String(a.accountId) === String(id));
                const platform = acc?.accountType || '';
                const campaignType = acc?.campaignType || '';
                setValue('medium', platform);
                setValue('campaignType', campaignType);
                const rawIdentifier = acc?.accountIdentifier || '';
                const identifierDisplay = (platform === 'Meta' && rawIdentifier.startsWith('act_')) ? rawIdentifier.slice(4) : rawIdentifier;
                setValue('accountIdentifier', identifierDisplay);
              }}>
                <option value="">Selecciona primero un cliente</option>
                {(accounts || []).map(a => (
                  <option key={a.accountId} value={a.accountId}>
                    {a.accountName}
                  </option>
                ))}
              </Form.Select>
            </Col>
            
            <Col md={6}>
              <Form.Label>Tipo de Cuenta</Form.Label>
              <Form.Control value={watch('campaignType') || ''} readOnly placeholder="Branding o Performance" />
            </Col>
            <Col md={6}>
              <Form.Label>Medio</Form.Label>
              <Form.Control value={watch('medium') || ''} readOnly placeholder="Se autocompleta por cuenta" />
            </Col>
          
            <Col md={6}>
              <Form.Label>ID de Cuenta</Form.Label>
              <Form.Control value={watch('accountIdentifier') || ''} readOnly placeholder="Se autocompleta por cuenta" />
            </Col>
            <Col md={6}>
              <Form.Label>Período de Inicio</Form.Label>
              <Form.Control type="date" {...register('startDate')} />
            </Col>
            <Col md={6}>
              <Form.Label>Período de Fin</Form.Label>
              <Form.Control type="date" {...register('endDate')} />
            </Col>
            <Col md={6}>
              <Form.Label>Unidad de Medición</Form.Label>
              <Form.Select {...register('unit')}>
                <option>Leads</option>
                <option>Registros Web</option>
                <option>WhatsApp</option>
                <option>Clicks</option>
                <option>Link Clicks</option>
                <option>Impresiones</option>
                <option>Vistas de Video</option>
                <option>Reach</option>
                <option>CPA</option>
                <option>CPM</option>
                <option>CPV</option>
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label>Valor Nominal Objetivo</Form.Label>
              <Form.Control type="number" step="any" {...register('nominalValue', { valueAsNumber: true })} />
            </Col>
            <Col md={6}>
              <Form.Label>Frecuencia Deseada</Form.Label>
              <Form.Select {...register('desiredFrequency')}>
                <option>Semanal</option>
                <option>Diario</option>
                <option>Mensual</option>
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label>Tipo de Objetivo</Form.Label>
              <Form.Select {...register('objectiveType')}>
                <option>Nominal</option>
                <option>Acumulado</option>
              </Form.Select>
            </Col>
            
            <Col md={6}>
              <Form.Label>Nivel de Urgencia</Form.Label>
              <Form.Select {...register('urgency')}>
                <option>Alta</option>
                <option>Media</option>
                <option>Baja</option>
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label>Responsable</Form.Label>
              <Form.Control placeholder="ej. Guido M." {...register('owner')} />
            </Col>
            <Col md={6} className="d-flex align-items-end">
              <Form.Check type="switch" label="Objetivo Acumulado" {...register('accumulated')} />
            </Col>
            <Col md={12}>
              <Form.Label>Comentario</Form.Label>
              <Form.Control as="textarea" rows={3} placeholder="Notas para el objetivo" {...register('comments')} />
            </Col>
          </Row>
          <div className="mt-4 d-flex gap-2">
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting && <Spinner size="sm" className="me-2" />}
              {editingObjective ? 'Actualizar Objetivo' : 'Guardar Objetivo'}
            </Button>
            {editingObjective && (
              <Button 
                type="button" 
                variant="outline-secondary" 
                onClick={() => {
                  reset(defaultValues);
                  setSelectedClientId('');
                  setAccounts([]);
                  setEditingObjective(null);
                }}
              >
                Nuevo objetivo
              </Button>
            )}
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default CreateObjectiveForm;


